import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { EmptyState } from '@pazportal/ui';
import { useRackPlant } from '../hooks/useRackPlant';
import { useDeviceRetirements, usePropertyManual } from '../hooks/useDeviceRetirements';
import { RackExplorer } from '../components/racks/RackExplorer';
import { RetireDeviceDialog, RetirementsDrawer } from '../components/racks/DeviceRetirement';
import { PreSurveyPlant } from '../components/racks/PreSurveyPlant';

export function LocationRacksPage() {
  const { id: locationId } = useParams<{ id: string }>();
  const { data: plant, isLoading, isError } = useRackPlant(locationId);
  // The explorer renders its query bar here, in the header's right corner.
  const [querySlot, setQuerySlot] = useState<HTMLDivElement | null>(null);
  const { data: retirements } = useDeviceRetirements(locationId);
  const { data: manual } = usePropertyManual(locationId);
  const [retiringUnitId, setRetiringUnitId] = useState<string | null>(null);
  // null = closed; '' = open on the list; an id = open with that retirement focused.
  const [drawer, setDrawer] = useState<string | null>(null);
  const unfiled = (retirements ?? []).filter((r) => !r.learn_article_url).length;

  return (
    <div className="flex h-[calc(100vh-5.5rem)] min-h-[640px] flex-col gap-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-xl font-bold text-text sm:text-2xl">Racks</h1>
          <p className="mt-1 text-sm text-text-muted">
            Every surveyed rack at this site, what sits in each unit, and where it connects.
          </p>
          <button
            type="button"
            onClick={() => setDrawer('')}
            className="mt-1 text-sm text-primary hover:underline"
          >
            Retired devices{retirements?.length ? ` (${retirements.length})` : ''}
            {unfiled > 0 && <span className="ml-1.5 rounded-sm bg-warning-soft px-1.5 py-0.5 text-[11px] text-warning">{unfiled} not filed in Learn</span>}
          </button>
        </div>
        <div ref={setQuerySlot} className="w-full lg:w-auto lg:min-w-[34rem] lg:max-w-2xl lg:flex-1" />
      </div>

      {isLoading ? (
        <p className="text-sm text-text-muted">Loading racks…</p>
      ) : isError ? (
        <EmptyState icon={<RackIcon />} title="Unable to load racks" subtitle="The rack survey for this site could not be read. Refresh to try again." />
      ) : !plant || (plant.racks.length === 0 && plant.rooms.length === 0) ? (
        <EmptyState
          icon={<RackIcon />}
          title="No racks recorded"
          subtitle="No network rooms or racks have been surveyed for this site yet."
        />
      ) : plant.racks.length === 0 ? (
        <div className="min-h-0 flex-1">
          <PreSurveyPlant plant={plant} />
        </div>
      ) : (
        <div className="min-h-0 flex-1">
          <RackExplorer plant={plant} queryBarSlot={querySlot} onRetireUnit={setRetiringUnitId} />
        </div>
      )}

      {plant && locationId && retiringUnitId && (
        <RetireDeviceDialog
          plant={plant}
          unitId={retiringUnitId}
          locationId={locationId}
          manual={manual}
          onClose={() => setRetiringUnitId(null)}
          onRetired={(id) => {
            setRetiringUnitId(null);
            setDrawer(id);
          }}
        />
      )}
      {locationId && drawer !== null && (
        <RetirementsDrawer locationId={locationId} manual={manual} focusId={drawer || null} onClose={() => setDrawer(null)} />
      )}
    </div>
  );
}

function RackIcon() {
  return (
    <svg className="h-12 w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
    </svg>
  );
}
